import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  toggleModal,
  setMaximumAlertPriority,
  setAlertNotificationConfigs,
} from "app/store/transmission/slice/transmissionSlice";

import { isValidArray } from "utils/array";
import { showErrorToast } from "utils/toast";
import { shouldSendLetterWithReportNote } from "utils/report";
import { ALERT_MESSAGE_CONTENT_LIMIT } from "static/constants";
import {
  isEmailConfig,
  getDefaultSelectedRecipients,
  generateNotificationConfigGroups,
} from "utils/notificationPreference";

export const useAlertNotification = ({ notes, handleReportNote }) => {
  const dispatch = useDispatch();

  const {
    reportCurrentTab,
    reportDetail,
    billableDate,
    saveWithProcess,
    reportAccountDetail,
    maximumAlertPriority,
    alertNotificationConfigs,
  } = useSelector((state) => state.transmission);

  const [submitting, setSubmitting] = useState({
    withNotify: false,
    withoutNotify: false,
  });

  const [configGroups, setConfigGroups] = useState([]);
  const [messageContents, setMessageContents] = useState({});

  const [selectedRecipients, setSelectedRecipients] = useState({});

  useEffect(() => {
    if (!maximumAlertPriority || alertNotificationConfigs?.length === 0) return;

    const { configGroups, messageContents } = generateNotificationConfigGroups(
      alertNotificationConfigs,
      maximumAlertPriority,
      reportDetail
    );

    setConfigGroups(configGroups);
    setMessageContents(messageContents);
    setSelectedRecipients((prev) => ({
      ...getDefaultSelectedRecipients(configGroups),
      ...prev,
    }));
  }, [maximumAlertPriority, alertNotificationConfigs, reportDetail]);

  const handleMessageChange = (key, value) => {
    setMessageContents((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRecipientsChange = (key, recipients) => {
    setSelectedRecipients((prev) => ({ ...prev, [key]: recipients }));
  };

  const handleAddNoteToMessage = (key, note) => {
    const current = messageContents[key] || "";
    const newValue = current ? `${current}; ${note}` : note;
    handleMessageChange(key, newValue);
  };

  const onClose = () => {
    dispatch(toggleModal({ type: "alertNotification", value: false }));
    dispatch(setMaximumAlertPriority(null));
    dispatch(setAlertNotificationConfigs(null));
    setMessageContents({});
    setSelectedRecipients({});
  };

  const handleSaveReportNotes = async (notify = false) => {
    const submittingKey = notify ? "withNotify" : "withoutNotify";

    try {
      setSubmitting((prev) => ({ ...prev, [submittingKey]: true }));

      const { isValidForSendingLetter } = shouldSendLetterWithReportNote({
        reportTab: reportCurrentTab,
        billableDate,
        report: reportDetail,
        notes: notes?.reportNotes || [],
      });

      const alertNotifications = notify
        ? configGroups?.flatMap(({ messageKey, configs }) =>
            configs?.map((config) => ({
              alert_type: maximumAlertPriority?.alert_type,
              recipients: isEmailConfig(config)
                ? selectedRecipients[messageKey]
                : config.recipients,
              time_period: config.time_period,
              notification_method: config.notification_method,
              message_content: messageContents[messageKey]?.trim(),
              timezone: reportAccountDetail?.timezone,
              include_transmission_pdf: Boolean(
                config?.include_transmission_pdf
              ),
            }))
          )
        : [];

      const reportNoteProps = {
        notes,
        billingDate: billableDate,
        isProcessAction: saveWithProcess,
        skipAlertNotification: !notify,
        ...(isValidArray(alertNotifications) && { alertNotifications }),
        shouldSendLetter: isValidForSendingLetter,
      };

      await handleReportNote(reportNoteProps);

      onClose();
    } catch (error) {
      showErrorToast(error?.message || error);
    } finally {
      setSubmitting((prev) => ({ ...prev, [submittingKey]: false }));
    }
  };

  const isDisabled = submitting.withNotify || submitting.withoutNotify;

  const areAllMessagesValid = Object.values(messageContents).every(
    (msg) => msg && msg.length <= ALERT_MESSAGE_CONTENT_LIMIT
  );

  // Each Email config needs at least one selected recipient
  const areEmailRecipientsSelected = configGroups.every(
    ({ messageKey, configs }) =>
      !configs?.some(isEmailConfig) ||
      isValidArray(selectedRecipients[messageKey])
  );

  const configs = configGroups.flatMap((group) => group.configs) || [];

  return {
    configs,
    configGroups,
    messageContents,
    submitting,
    isDisabled,
    areAllMessagesValid,
    selectedRecipients,
    areEmailRecipientsSelected,
    handleMessageChange,
    handleRecipientsChange,
    handleAddNoteToMessage,
    handleSaveReportNotes,
    onClose,
  };
};

export default useAlertNotification;
